import axios from 'axios';
import { BASE_URL, buildShortsWorkOrderFeedUrl, SHORTS_PRESET_API_PATH } from 'constants/api';
import { FetchResult } from 'type/apiType';
import { ShortsPresetInterface, ShortsPresetRequest } from '../types/shortsPresetType';

const PRESET_URL = `${BASE_URL}${SHORTS_PRESET_API_PATH}`;

/**
 * Field errors as returned by the backend: `{ errors: { <field>: ["msg", ...] } }`.
 * i18nApiErrorHandler is not reused here — it only understands i18n field shapes.
 */
export type PresetFieldErrors = Record<string, string[]>;

class ShortsPresetService {
  public async fetchPresets(params?: URLSearchParams): Promise<FetchResult<ShortsPresetInterface>> {
    const response = await axios.get(PRESET_URL, { params });
    return response.data;
  }

  public async fetchPresetById(id: number): Promise<ShortsPresetInterface> {
    const response = await axios.get(`${PRESET_URL}${id}/`);
    return response.data;
  }

  public async createPreset(data: ShortsPresetRequest): Promise<ShortsPresetInterface> {
    const response = await axios.post(PRESET_URL, data);
    return response.data;
  }

  /** platform_id is create-only, so callers must not include it in the update payload. */
  public async updatePreset(
    id: number,
    data: Omit<ShortsPresetRequest, 'platform_id'>
  ): Promise<ShortsPresetInterface> {
    const response = await axios.patch(`${PRESET_URL}${id}/`, data);
    return response.data;
  }

  public async deletePreset(id: number): Promise<void> {
    await axios.delete(`${PRESET_URL}${id}/`);
  }

  /**
   * Work-order feed of one playlist, as raw MRSS XML.
   *
   * The endpoint is public, so this is only about rendering the feed in-app instead of
   * sending the operator to a raw XML tab. It takes no parameters — the window is fixed
   * at 3 days server-side. `transformResponse` is neutralised because the default one
   * attempts JSON.parse on the XML.
   */
  public async fetchWorkOrderFeed(playlistId: number): Promise<string> {
    try {
      const response = await axios.get<string>(buildShortsWorkOrderFeedUrl(playlistId), {
        responseType: 'text',
        transformResponse: [(data: string) => data],
      });
      return response.data;
    } catch (error) {
      // Error bodies come back as raw strings too, since transformResponse applies to
      // them as well. Parse it back so the shared `errors`/`message` readers still work.
      throw parseStringErrorBody(error);
    }
  }
}

/**
 * Replace a JSON-string error body with the parsed object, in place.
 * Leaves the error untouched if the body isn't a string or isn't JSON.
 */
const parseStringErrorBody = (error: unknown): unknown => {
  const response = (error as { response?: { data?: unknown } })?.response;
  if (!response || typeof response.data !== 'string') {
    return error;
  }
  try {
    response.data = JSON.parse(response.data);
  } catch {
    // Non-JSON body (an HTML error page, say) — leave it as the string it is.
  }
  return error;
};

export const shortsPresetService = new ShortsPresetService();

/**
 * Pull `errors.<field>: [...]` out of a 400 response.
 * Returns an empty object when the payload doesn't carry field errors.
 */
export const extractPresetFieldErrors = (error: unknown): PresetFieldErrors => {
  const errors = (error as { response?: { data?: { errors?: unknown } } })?.response?.data?.errors;
  if (!errors || typeof errors !== 'object' || Array.isArray(errors)) {
    return {};
  }

  return Object.entries(errors as Record<string, unknown>).reduce<PresetFieldErrors>(
    (acc, [field, messages]) => {
      if (Array.isArray(messages)) {
        acc[field] = messages.map(String);
      } else if (typeof messages === 'string') {
        acc[field] = [messages];
      }
      return acc;
    },
    {}
  );
};

/** Flatten field errors into a single line, for toasts and non-form surfaces. */
export const formatPresetErrorMessage = (error: unknown, fallback: string): string => {
  const fieldErrors = extractPresetFieldErrors(error);
  const entries = Object.entries(fieldErrors);
  if (entries.length > 0) {
    return entries.map(([field, messages]) => `${field}: ${messages.join(' ')}`).join(' / ');
  }

  const message = (error as { response?: { data?: { message?: unknown } } })?.response?.data
    ?.message;
  return typeof message === 'string' && message ? message : fallback;
};
